import cx from 'classnames';
import { useState, useRef } from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';
import IsServerSideRendering from '~/utilities/IsServerSideRendering';

const Portal = ({ component, open }) => {
  if (!open) {
    return null;
  }

  let target = document.querySelector('#tooltip-root');
  if (!target) {
    target = document.createElement('div');
    target.id = 'tooltip-root';
    document.body.appendChild(target);
  }

  return createPortal(component, target);
};

type Offset = { x: number; y: number; width: number; height: number };

const createTooltip = (
  component: React.ReactNode,
  className: string,
  direction: 'left' | 'right',
  offset: Offset
) => {
  if (IsServerSideRendering) {
    return null;
  }

  const { pageYOffset, pageXOffset } = window;

  const offsetY = offset.y + pageYOffset + offset.height + 5;

  let offsetX = offset.x + pageXOffset;
  if (direction === 'right') {
    offsetX = offsetX + offset.width - 230;
  }

  const classes = cx('tooltip', direction, { [className]: className });
  const style = { top: offsetY, left: offsetX };

  return (
    <div className={classes} style={style}>
      {component}
    </div>
  );
};

type OwnProps = React.PropsWithChildren<{
  className: string;
  component?: React.ReactNode;
  right?: boolean;
}>;

export default function Tooltip({
  children,
  className,
  component,
  right = false
}: OwnProps) {
  const [offset, setOffset] = useState<Offset>({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const tooltipRef = useRef<HTMLElement>(null);

  const handleShowTooltip = () => {
    if (showTooltip || tooltipRef.current == null) return null;

    const { x, y, width, height } = tooltipRef.current.getBoundingClientRect();
    setOffset({ x, y, width, height });
    setShowTooltip(true);
  };

  const handleHideTooltip = () => {
    setShowTooltip(false);
  };

  const tooltip = createTooltip(
    component,
    className,
    right ? 'right' : 'left',
    offset
  );

  return (
    <span
      ref={tooltipRef}
      className="tooltip-anchor"
      onMouseOver={handleShowTooltip}
      onFocus={handleShowTooltip}
      onMouseOut={handleHideTooltip}
      onBlur={handleHideTooltip}
      onTouchStart={handleShowTooltip}
      onTouchEnd={handleHideTooltip}
    >
      {children}
      {showTooltip && <Portal component={tooltip} open={showTooltip} />}
    </span>
  );
}
